import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  serverTimestamp
} from 'firebase/firestore';
import {
  createCondoCustomItemId,
  getCondoCustomItemKey,
  normalizeCondoDetailsCustom
} from '../constants/condoDetails';

const SETTINGS_COLLECTION = 'settings';
const CATALOG_DOC_ID = 'condoDetailsCustom';

function catalogDocRef(db) {
  return doc(db, SETTINGS_COLLECTION, CATALOG_DOC_ID);
}

/** Mescla catálogo global com opções legadas salvas no documento do condomínio. */
export function mergeCondoDetailsCustomCatalog(globalCatalog = [], legacyCatalog = []) {
  const byId = new Map();
  normalizeCondoDetailsCustom(globalCatalog).forEach((item) => byId.set(item.id, item));
  normalizeCondoDetailsCustom(legacyCatalog).forEach((item) => {
    if (!byId.has(item.id)) byId.set(item.id, item);
  });
  return Array.from(byId.values());
}

export async function fetchGlobalCondoDetailsCustomOptions() {
  const db = getFirestore();
  const snap = await getDoc(catalogDocRef(db));
  if (!snap.exists()) return [];
  return normalizeCondoDetailsCustom(snap.data()?.options);
}

export async function saveGlobalCondoDetailsCustomOptions(options) {
  const db = getFirestore();
  const normalized = normalizeCondoDetailsCustom(options);
  await setDoc(
    catalogDocRef(db),
    {
      options: normalized,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  return normalized;
}

export async function addGlobalCondoDetailsCustomOption({ topic, label }) {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (!trimmed) return null;

  const current = await fetchGlobalCondoDetailsCustomOptions();
  const duplicate = current.find(
    (item) => item.topic === topic && item.label.toLowerCase() === trimmed.toLowerCase()
  );
  if (duplicate) return duplicate;

  const newItem = {
    id: createCondoCustomItemId(),
    topic,
    label: trimmed
  };
  await saveGlobalCondoDetailsCustomOptions([...current, newItem]);
  return newItem;
}

export async function removeGlobalCondoDetailsCustomOption(id) {
  const current = await fetchGlobalCondoDetailsCustomOptions();
  const next = current.filter((item) => item.id !== id);
  await saveGlobalCondoDetailsCustomOptions(next);
  return next;
}

const CONDO_DETAILS_USAGE_COLLECTIONS = ['condominiums', 'properties'];

function accumulateCustomOptionUsage(usage, condoDetails) {
  const cd = condoDetails || {};
  Object.entries(cd).forEach(([key, value]) => {
    if (value === true && key.startsWith('custom_')) {
      const id = key.slice('custom_'.length);
      usage[id] = (usage[id] || 0) + 1;
    }
  });
}

/** Quantos cadastros (condomínios + imóveis) têm a opção marcada em `condoDetails`. */
export async function countRegistrationsUsingCustomOption(customId) {
  const key = getCondoCustomItemKey(customId);
  const db = getFirestore();
  let count = 0;
  for (const collectionName of CONDO_DETAILS_USAGE_COLLECTIONS) {
    const snap = await getDocs(collection(db, collectionName));
    for (const docSnap of snap.docs) {
      if (docSnap.data()?.condoDetails?.[key] === true) count += 1;
    }
  }
  return count;
}

/** @deprecated Use countRegistrationsUsingCustomOption */
export async function countCondominiumsUsingCustomOption(customId) {
  return countRegistrationsUsingCustomOption(customId);
}

/** Mapa `id -> quantidade de cadastros` que usam cada opção personalizada. */
export async function getCondoCustomOptionUsageMap() {
  const db = getFirestore();
  const usage = {};
  for (const collectionName of CONDO_DETAILS_USAGE_COLLECTIONS) {
    const snap = await getDocs(collection(db, collectionName));
    snap.forEach((docSnap) => {
      accumulateCustomOptionUsage(usage, docSnap.data()?.condoDetails);
    });
  }
  return usage;
}

/** Promove opções legadas do documento para o catálogo global (sem duplicar). */
export async function migrateLegacyCondoDetailsCustomToGlobal(legacyItems = []) {
  const legacy = normalizeCondoDetailsCustom(legacyItems);
  if (legacy.length === 0) return fetchGlobalCondoDetailsCustomOptions();

  const current = await fetchGlobalCondoDetailsCustomOptions();
  const existingIds = new Set(current.map((item) => item.id));
  const toAdd = legacy.filter((item) => !existingIds.has(item.id));
  if (toAdd.length === 0) return current;

  return saveGlobalCondoDetailsCustomOptions([...current, ...toAdd]);
}
